import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AnalyticsEventType } from '../models/analytics';
import { modifiedSetInterval } from '../utilities/ZoneUtils';
import { FETCH_VIDEO_CALL_STATS_INTERVAL } from '../utilities/webrtc-stats-reporter/webrtc-stats-reporter-types';
import { AnalyticsService } from './analytics.service';
import { FLAGS, FlagsService } from './flags.service';
import { ProviderStateService } from './provider-state.service';

interface SystemStatusAnalyticsMetadata extends Record<string, unknown> {
  isNetworkStable: boolean;
  isDeviceStruggling: boolean;
  micError?: string;
  camError?: string;
}

interface AggregatedSystemStatusMetrics extends Record<string, unknown> {
  networkUnstableDuration: number;
  deviceStrugglingDuration: number;
  micErrorDuration: number;
  camErrorDuration: number;
  micErrorTypes: Set<string>;
  camErrorTypes: Set<string>;
}

const SYSTEM_STATUS_INTERVAL = FETCH_VIDEO_CALL_STATS_INTERVAL / 1000; // Each status event represents 10 seconds

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SystemStatusAnalyticsService {
  private readonly ANALYTICS_BUFFER_INTERVAL = 60 * 1000; // 1 minute in milliseconds
  private systemStatusBuffer: SystemStatusAnalyticsMetadata[] = [];
  private systemStatusAnalyticsInterval?: NodeJS.Timer;

  constructor(
    private analyticsService: AnalyticsService,
    private providerStateService: ProviderStateService,
    private flagsService: FlagsService,
  ) {
    // Listen to call connection state to start/stop analytics
    this.providerStateService.callConnected$.pipe(untilDestroyed(this)).subscribe((isConnected) => {
      if (isConnected) {
        this.startSystemStatusAnalyticsInterval();
      } else {
        this.stopSystemStatusAnalyticsInterval();
      }
    });
  }

  /**
   * Add system status metrics to the buffer
   */
  addToSystemStatusBuffer(status: SystemStatusAnalyticsMetadata): void {
    if (!this.isAnalyticsEnabled()) {
      return;
    }

    this.systemStatusBuffer.push(status);
  }

  private startSystemStatusAnalyticsInterval(): void {
    if (!this.isAnalyticsEnabled() || this.systemStatusAnalyticsInterval) {
      return;
    }

    this.systemStatusAnalyticsInterval = modifiedSetInterval(
      () => {
        this.processSystemStatusBuffer();
      },
      this.ANALYTICS_BUFFER_INTERVAL,
      true,
    );
  }

  private stopSystemStatusAnalyticsInterval(): void {
    if (this.systemStatusAnalyticsInterval) {
      clearInterval(this.systemStatusAnalyticsInterval);
      this.systemStatusAnalyticsInterval = undefined;
    }
    // Process any remaining items in buffer
    this.processSystemStatusBuffer();
    // Clear the buffer
    this.systemStatusBuffer = [];
  }

  private processSystemStatusBuffer(): void {
    if (this.systemStatusBuffer.length === 0) {
      return;
    }

    // Aggregate metrics over the buffer period
    const aggregatedMetrics = this.systemStatusBuffer.reduce<AggregatedSystemStatusMetrics>(
      (metrics, status) => ({
        networkUnstableDuration:
          metrics.networkUnstableDuration + (!status.isNetworkStable ? SYSTEM_STATUS_INTERVAL : 0),
        deviceStrugglingDuration:
          metrics.deviceStrugglingDuration +
          (status.isDeviceStruggling ? SYSTEM_STATUS_INTERVAL : 0),
        micErrorDuration: metrics.micErrorDuration + (status.micError ? SYSTEM_STATUS_INTERVAL : 0),
        camErrorDuration: metrics.camErrorDuration + (status.camError ? SYSTEM_STATUS_INTERVAL : 0),
        micErrorTypes: status.micError
          ? metrics.micErrorTypes.add(status.micError)
          : metrics.micErrorTypes,
        camErrorTypes: status.camError
          ? metrics.camErrorTypes.add(status.camError)
          : metrics.camErrorTypes,
      }),
      {
        networkUnstableDuration: 0,
        deviceStrugglingDuration: 0,
        micErrorDuration: 0,
        camErrorDuration: 0,
        micErrorTypes: new Set<string>(),
        camErrorTypes: new Set<string>(),
      },
    );

    // Send aggregated metrics as a single event
    this.analyticsService.addToAnalyticsEventsBatch(
      AnalyticsEventType.USER_SYSTEM_STATUS,
      undefined,
      {
        networkUnstableDuration: aggregatedMetrics.networkUnstableDuration,
        deviceStrugglingDuration: aggregatedMetrics.deviceStrugglingDuration,
        micErrorDuration: aggregatedMetrics.micErrorDuration,
        camErrorDuration: aggregatedMetrics.camErrorDuration,
        micErrorTypes: Array.from(aggregatedMetrics.micErrorTypes),
        camErrorTypes: Array.from(aggregatedMetrics.camErrorTypes),
      },
    );

    // Clear the buffer after processing
    this.systemStatusBuffer = [];
  }

  private isAnalyticsEnabled(): boolean {
    return (
      this.flagsService.isFlagEnabled(FLAGS.ENABLE_NETWORK_INDICATOR) &&
      !!this.flagsService.featureFlagsVariables[FLAGS.ENABLE_NETWORK_INDICATOR]
        ?.analytics_integration
    );
  }
}
